import type { AxiosInstance } from "axios";

import { parseAxiosResponse } from "@thalamos/common";
import type {
  AttachOrganisationsRequest,
  ChangeSettingsRequest,
  ConfigurationScope,
  ConfigurationScopeId,
  ConfigurationScopeList,
  EffectiveConfiguration,
  HolderConfiguration,
  OrganisationId,
  OrganisationScopeList,
  OrganisationType,
  RealtimeAuthorisation,
  RealtimeAuthorisationRequest,
  RealtimeSettings,
  WriteConfigurationScopeRequest,
} from "@thalamos/haven-common";
import {
  ConfigurationScopeListSchema,
  ConfigurationScopeSchema,
  EffectiveConfigurationSchema,
  HolderConfigurationSchema,
  OrganisationScopeListSchema,
  RealtimeAuthorisationSchema,
  RealtimeSettingsSchema,
} from "@thalamos/haven-common";

import type { ErrorBody, ResponseBody } from "../axios/api.js";
import { useApiAxios } from "../axios/api.js";

const parseResponseEffective = parseAxiosResponse(EffectiveConfigurationSchema);
const parseResponseHolder = parseAxiosResponse(HolderConfigurationSchema);
const parseResponseScope = parseAxiosResponse(ConfigurationScopeSchema);
const parseResponseScopeList = parseAxiosResponse(ConfigurationScopeListSchema);

/**
 * A created configuration answers 201, which `parseAxiosResponse` leaves alone,
 * so the body is parsed here when it arrives. A refusal keeps its error body.
 */
const parseCreatedScope = (response: {
  status: number;
  data: unknown;
}): ResponseBody<ConfigurationScope, ErrorBody> =>
  response.status === 201
    ? ConfigurationScopeSchema.parse(response.data)
    : (response.data as ErrorBody);
const parseResponseOrganisationScopes = parseAxiosResponse(
  OrganisationScopeListSchema,
);
const parseResponseRealtimeSettings = parseAxiosResponse(
  RealtimeSettingsSchema,
);
const parseResponseRealtimeAuthorisation = parseAxiosResponse(
  RealtimeAuthorisationSchema,
);

/** A page of a listing, as a caller asks for it. */
export type OrganisationScopeQuery = {
  query?: string;
  configurationScopeId?: string;
  memberOfOrganisationId?: OrganisationId;
  type?: readonly OrganisationType[];
  limit?: number;
  offset?: number;
};

/**
 * Only the parameters a caller actually named are sent, so the server's defaults
 * stay the single definition of a page size.
 */
const listParams = ({
  query,
  configurationScopeId,
  memberOfOrganisationId,
  type,
  limit,
  offset,
}: OrganisationScopeQuery) => ({
  ...(query !== undefined && query.length > 0 ? { query } : {}),
  ...(configurationScopeId !== undefined ? { configurationScopeId } : {}),
  ...(memberOfOrganisationId !== undefined ? { memberOfOrganisationId } : {}),
  ...(type !== undefined && type.length > 0 ? { type } : {}),
  ...(limit !== undefined ? { limit } : {}),
  ...(offset !== undefined ? { offset } : {}),
});

export const config = {
  configuration: {
    /** How this deployment publishes live updates — see `RealtimeSettings`. */
    realtime: () => ({
      url: "/api/configuration/realtime",
      method: "get",
      transformResponse: parseResponseRealtimeSettings,
    }),

    /**
     * Asks to be admitted to one live channel.
     *
     * Made with this application's own HTTP client rather than through the
     * realtime library's: its request carries no credentials and offers no way
     * to add them, so a session held in a cookie never reaches the server. This
     * way the call also inherits the 401 handling and the version check every
     * other call gets.
     */
    realtimeAuthorisation: (body: RealtimeAuthorisationRequest) => ({
      url: "/api/realtime/authorisations",
      method: "post",
      data: body,
      transformResponse: parseResponseRealtimeAuthorisation,
    }),

    /**
     * What the body this session is acting for is configured as.
     *
     * Flat, with no provenance: the server folds the chain, so there is nothing
     * here to fold and nothing to disagree with it about.
     */
    effective: () => ({
      url: "/api/configuration",
      method: "get",
      transformResponse: parseResponseEffective,
    }),
    scopes: () => ({
      url: "/api/admin/configuration/scopes",
      method: "get",
      transformResponse: parseResponseScopeList,
    }),
    /**
     * Answers 201, which `parseAxiosResponse` deliberately does not parse — it
     * validates a 200 and passes every other status through, so a refusal reaches
     * the caller as the error shape it is. So there is no transform here: the
     * request parses the body with `parseCreatedScope` when it arrives.
     */
    createScope: (body: WriteConfigurationScopeRequest) => ({
      url: "/api/admin/configuration/scopes",
      method: "post",
      data: body,
    }),
    updateScope: (
      scopeId: ConfigurationScopeId,
      body: WriteConfigurationScopeRequest,
    ) => ({
      url: `/api/admin/configuration/scopes/${scopeId}`,
      method: "put",
      data: body,
      transformResponse: parseResponseScope,
    }),
    scopeSettings: (scopeId: string) => ({
      url: `/api/admin/configuration/scopes/${scopeId}/settings`,
      method: "get",
      transformResponse: parseResponseHolder,
    }),
    /**
     * Answers with the holder's whole configuration rather than the settings sent,
     * because changing one value can change what another resolves to — a set
     * narrowing under a body, a default language reconciled against it. A reply
     * carrying only the fields that were sent would leave the page to guess at the
     * rest.
     */
    changeScopeSettings: (
      scopeId: ConfigurationScopeId,
      body: ChangeSettingsRequest,
    ) => ({
      url: `/api/admin/configuration/scopes/${scopeId}/settings`,
      method: "patch",
      data: body,
      transformResponse: parseResponseHolder,
    }),
    changeOrganisationSettings: (
      organisationId: OrganisationId,
      body: ChangeSettingsRequest,
    ) => ({
      url: `/api/admin/configuration/organisations/${organisationId}/settings`,
      method: "patch",
      data: body,
      transformResponse: parseResponseHolder,
    }),
    attachOrganisations: (body: AttachOrganisationsRequest) => ({
      url: "/api/admin/configuration/organisations/scope-changes",
      method: "post",
      data: body,
    }),
    organisations: (request: OrganisationScopeQuery = {}) => ({
      url: "/api/admin/configuration/organisations",
      method: "get",
      params: listParams(request),
      transformResponse: parseResponseOrganisationScopes,
    }),
    organisationSettings: (organisationId: string) => ({
      url: `/api/admin/configuration/organisations/${organisationId}/settings`,
      method: "get",
      transformResponse: parseResponseHolder,
    }),
  },
};

export const api = (apiAxios: AxiosInstance) => {
  return {
    configuration: {
      realtime: () =>
        apiAxios.request<RealtimeSettings>(config.configuration.realtime()),
      realtimeAuthorisation: (body: RealtimeAuthorisationRequest) =>
        apiAxios.request<RealtimeAuthorisation>(
          config.configuration.realtimeAuthorisation(body),
        ),
      effective: () =>
        apiAxios.request<ResponseBody<EffectiveConfiguration, ErrorBody>>(
          config.configuration.effective(),
        ),
      scopes: () =>
        apiAxios.request<ResponseBody<ConfigurationScopeList, ErrorBody>>(
          config.configuration.scopes(),
        ),
      createScope: async (body: WriteConfigurationScopeRequest) => {
        const response = await apiAxios.request<unknown>(
          config.configuration.createScope(body),
        );

        return { status: response.status, data: parseCreatedScope(response) };
      },
      updateScope: (
        scopeId: ConfigurationScopeId,
        body: WriteConfigurationScopeRequest,
      ) =>
        apiAxios.request<ResponseBody<ConfigurationScope, ErrorBody>>(
          config.configuration.updateScope(scopeId, body),
        ),
      scopeSettings: (scopeId: string) =>
        apiAxios.request<ResponseBody<HolderConfiguration, ErrorBody>>(
          config.configuration.scopeSettings(scopeId),
        ),
      changeScopeSettings: (
        scopeId: ConfigurationScopeId,
        body: ChangeSettingsRequest,
      ) =>
        apiAxios.request<ResponseBody<HolderConfiguration, ErrorBody>>(
          config.configuration.changeScopeSettings(scopeId, body),
        ),
      changeOrganisationSettings: (
        organisationId: OrganisationId,
        body: ChangeSettingsRequest,
      ) =>
        apiAxios.request<ResponseBody<HolderConfiguration, ErrorBody>>(
          config.configuration.changeOrganisationSettings(organisationId, body),
        ),
      attachOrganisations: (body: AttachOrganisationsRequest) =>
        apiAxios.request<ResponseBody<undefined, ErrorBody>>(
          config.configuration.attachOrganisations(body),
        ),
      organisations: (request: OrganisationScopeQuery = {}) =>
        apiAxios.request<ResponseBody<OrganisationScopeList, ErrorBody>>(
          config.configuration.organisations(request),
        ),
      organisationSettings: (organisationId: string) =>
        apiAxios.request<ResponseBody<HolderConfiguration, ErrorBody>>(
          config.configuration.organisationSettings(organisationId),
        ),
    },
  };
};

export const apiHooks = () => {
  return {
    configuration: {
      realtime: function useRealtimeSettings() {
        return useApiAxios<RealtimeSettings>(config.configuration.realtime());
      },
      effective: function useEffectiveConfiguration() {
        return useApiAxios<EffectiveConfiguration>(
          config.configuration.effective(),
        );
      },
      scopes: function useConfigurationScopes(
        options?: Parameters<typeof useApiAxios<ConfigurationScopeList>>[1],
      ) {
        return useApiAxios<ConfigurationScopeList>(
          config.configuration.scopes(),
          options,
        );
      },
      scopeSettings: function useScopeSettings(scopeId: string) {
        return useApiAxios<HolderConfiguration>(
          config.configuration.scopeSettings(scopeId),
        );
      },
      organisations: function useOrganisationScopes(
        request: OrganisationScopeQuery = {},
      ) {
        return useApiAxios<OrganisationScopeList>(
          config.configuration.organisations(request),
        );
      },
      organisationSettings: function useOrganisationSettings(
        organisationId: string,
        options?: Parameters<typeof useApiAxios<HolderConfiguration>>[1],
      ) {
        return useApiAxios<HolderConfiguration>(
          config.configuration.organisationSettings(organisationId),
          options,
        );
      },
    },
  };
};
